import { useIntl } from 'react-intl'
import { MouseEvent, useMemo } from 'react'
import { MenuItem, Typography } from '@carto/meridian-ds/components'
import { Box, Tooltip, Divider, ListItemText, ListItemIcon, styled } from '@mui/material'
import { ContextMenuItem } from './interfaces'

const MenuItemWrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'fixed' })<{ fixed?: boolean }>(
  ({ fixed, theme }) => ({
    '.MuiButtonBase-root.MuiMenuItem-root:first-of-type': {
      marginTop: 0
    },
    '&:first-of-type': {
      marginTop: theme.spacing(1)
    },

    ...(fixed && {
      position: 'sticky',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      zIndex: 2,
      minHeight: theme.spacing(6),
      marginBottom: theme.spacing(1),
      padding: theme.spacing(0.5, 1.5),
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,

      '&:first-of-type': {
        marginTop: 0
      },
      '.MuiMenuItem-root': {
        margin: 0,
        padding: 0,

        '&:hover': {
          backgroundColor: theme.palette.background.paper
        }
      }
    })
  })
)

interface Props {
  dense?: boolean
  extended?: boolean
  item: ContextMenuItem
  selected?: ContextMenuItem
  clickItem: (item: ContextMenuItem, event: MouseEvent) => unknown
}

export default function ContextMenuBaseItem({ item, selected, clickItem, dense, extended }: Props) {
  const intl = useIntl()

  const isSelected = useMemo(() => {
    if (selected && selected.direction) return selected.id === item.id && selected.direction === item.direction
    return selected?.id === item.id
  }, [item, selected])

  return (
    <>
      {!!item.divider && <Divider />}
      {item.label && (
        <Tooltip
          placement={item.tooltipPlacement || 'left'}
          enterDelay={item.tooltipEnterDelay || 500}
          title={
            item.tooltipId ? intl.formatMessage({ id: item.tooltipId }) : item.tooltipValue ? item.tooltipValue : ''
          }
          PopperProps={{
            style: {
              zIndex: 9999
            }
          }}
        >
          <MenuItemWrapper fixed={item.fixed}>
            <MenuItem
              aria-label={item.ariaLabel || (item.label as string)}
              onClick={(e) => (item.disabled ? undefined : clickItem(item, e))}
              disabled={item.disabled}
              selected={isSelected}
              dense={dense}
              extended={extended}
              destructive={item.destructive}
              subtitle={item.subtitle}
              iconColor={item.iconColor}
            >
              {item.startAdornment && <ListItemIcon>{item.startAdornment}</ListItemIcon>}
              <ListItemText>
                {item.label}
                {item.description && (
                  <Typography component="p" noWrap variant="caption" color="text.secondary">
                    {item.description}
                  </Typography>
                )}
              </ListItemText>
              {item.endAdornment && <Box>{item.endAdornment}</Box>}
            </MenuItem>
          </MenuItemWrapper>
        </Tooltip>
      )}
    </>
  )
}
